/**
 * UnavailableMethodsNote — the methods a sign-in or an unlock offers but cannot take right now, each with its reason
 * (POS-OFFLINE-STATION-LOCK-001 U14'd-2: a locked register that cannot reach the server unlocks only with a proof it
 * holds, and shows what it does not hold as unavailable with the reason, never hidden). The selector's pills are
 * described by `hintId`; this note is what that hint points the reader to.
 *
 * @module @puffin-core/login-shell/components/UnavailableMethodsNote
 */

import PropTypes from 'prop-types';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';
import { methodLabelKey } from './AuthMethodSelector.jsx';

/**
 * @param {Object} props
 * @param {ReadonlyArray<{method: string, reason: string}>} props.unavailableMethods - in the order the methods are
 *   offered; `reason` is already translated by the caller
 * @param {(key: string, params?: object) => string|undefined|null} [props.t] - the host's translator (`useLoginTranslate`)
 * @returns {React.JSX.Element|null}
 */
export function UnavailableMethodsNote({ unavailableMethods, t }) {
  const translate = useLoginTranslate(t);
  if (unavailableMethods.length === 0) return null;
  return (
    <ul
      className="list-unstyled text-sm text-muted mb-md"
      aria-label={translate('method.unavailableTitle')}
      data-testid="auth-method-unavailable"
    >
      {unavailableMethods.map(({ method, reason }) => (
        <li key={method} data-method={method}>
          {translate('method.unavailableLine', {
            method: translate(methodLabelKey(method)),
            reason,
          })}
        </li>
      ))}
    </ul>
  );
}

UnavailableMethodsNote.propTypes = {
  /** In the order the methods are offered; `reason` is already translated by the caller. */
  unavailableMethods: PropTypes.arrayOf(
    PropTypes.shape({ method: PropTypes.string.isRequired, reason: PropTypes.string.isRequired }),
  ).isRequired,
  t: PropTypes.func,
};

export default UnavailableMethodsNote;
